import { useWatch } from "react-hook-form";
import CuoButton from "@/app/admin/_common/CuoButton";
import { Input } from "@/app/admin/_common/ui/input";
import { Label } from "@/app/admin/_common/ui/label";
import { formatUtcDate } from "@/app/_utils/dayjs";
import { CalendarDays } from "lucide-react";
import {
  NameInput,
  RedirectInput,
  SourceInput,
  MediumInput,
  PreviewTitleInput,
  PreviewDescriptionInput,
  PreviewImageInput,
} from "../../_cmp/linkForm/LinkInputs";
import { useLinkFormFlow } from "../../_cmp/linkForm/useLinkFormFlow";
import { useLinkTaxonomy } from "../../_cmp/linkForm/useLinkTaxonomy";
import SharePreviewCard from "./SharePreviewCard";
import type { Link, LinkPreview } from "../../linksPageModel";

export default function LinkDetailsForm({
  link,
  preview,
  onSaved,
}: {
  link: Link;
  preview: LinkPreview | null;
  onSaved: () => void;
}) {
  const {
    control,
    errors,
    isSaving,
    isDirty,
    handleSave,
    discardChanges,
    selectedImage,
    setSelectedImage,
  } = useLinkFormFlow({ defaultLink: link, onSaved });
  const { mediums, sources } = useLinkTaxonomy();

  // Drive the preview card from the live field values so it tracks edits
  // before they're saved.
  const previewTitle = useWatch({ control, name: "preview_title" });
  const previewDescription = useWatch({ control, name: "preview_description" });

  const cardImage =
    selectedImage === "" ? null : selectedImage ?? link.preview_img_url ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-1">
        <div className="text-[16px] font-semibold text-foreground">Details</div>
        <div className="text-[12.5px] text-muted-foreground">
          Where this link sends people, and how the visit is tagged in analytics.
        </div>
      </div>

      <form id="update-link-form" onSubmit={(e) => e.preventDefault()}>
        <div className="grid gap-4 sm:grid-cols-2">
          <NameInput control={control} errors={errors} />
          <RedirectInput control={control} errors={errors} />
          <SourceInput control={control} errors={errors} options={sources} />
          <MediumInput control={control} errors={errors} options={mediums} />

          <div className="grid gap-3">
            <Label htmlFor="created">Created</Label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">
                <CalendarDays className="size-3.5" />
              </span>
              {/* readOnly rather than disabled: disabled dims the text to 50%
                  opacity, and this value should read as content, not as an
                  unavailable field. */}
              <Input
                id="created"
                value={formatUtcDate(link.created_at)}
                readOnly
                className="cursor-default pl-8 text-foreground"
              />
            </div>
          </div>
        </div>

        <div className="mt-16 grid gap-1">
          <div className="text-[16px] font-semibold text-foreground">Share preview</div>
          <div className="text-[12.5px] text-muted-foreground">
            How this link looks when it's texted or posted. Leave blank to use the linked
            show's artwork.
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="grid content-start gap-4">
            <PreviewTitleInput control={control} errors={errors} />
            <PreviewDescriptionInput control={control} errors={errors} />
            <PreviewImageInput
              currentImage={link.preview_img_url}
              onSelect={setSelectedImage}
            />
          </div>

          <SharePreviewCard
            preview={preview}
            title={previewTitle}
            description={previewDescription}
            image={cardImage}
          />
        </div>
      </form>

      {isDirty && (
        <div className="sticky bottom-5 z-10 flex w-full items-center justify-between gap-3 rounded-md border border-border bg-card px-5 py-3">
          <span className="text-[12.5px] text-muted-foreground">You have unsaved changes</span>
          <div className="flex items-center gap-2">
            <CuoButton
              variant="outline"
              label="Discard"
              onClick={discardChanges}
              disabled={isSaving}
            />
            <CuoButton
              variant="default"
              label="Save"
              onClick={handleSave}
              disableOnAsync={isSaving}
            />
          </div>
        </div>
      )}
    </div>
  );
}
